"use client";

import * as React from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "./input-group";

export type PasswordInputProps = Omit<React.ComponentProps<"input">, "size"> & {
	isInvalid?: boolean;
	/** Height/text size of the field. `lg` matches the legacy jux-next input. @default "default" */
	size?: "default" | "lg";
};

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
	({ className, isInvalid, disabled, size = "default", ...props }, ref) => {
		const [visible, setVisible] = React.useState(false);

		return (
			<InputGroup
				size={size}
				className={cn(className)}
				data-disabled={disabled || undefined}
			>
				<InputGroupInput
					ref={ref}
					type={visible ? "text" : "password"}
					size={size}
					disabled={disabled}
					aria-invalid={isInvalid || undefined}
					{...props}
				/>
				<InputGroupAddon align="inline-end">
					<InputGroupButton
						size={size === "lg" ? "icon-sm" : "icon-xs"}
						disabled={disabled}
						onClick={() => setVisible((v) => !v)}
						aria-label={visible ? "Hide password" : "Show password"}
					>
						{visible ? (
							<EyeOffIcon className="size-4" />
						) : (
							<EyeIcon className="size-4" />
						)}
					</InputGroupButton>
				</InputGroupAddon>
			</InputGroup>
		);
	},
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
